import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, Sparkles, Clock, Eye, PieChart, Presentation, ExternalLink, X, Plus, Mail, MessageCircle, Briefcase, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import { formatCurrency as marketFormatCurrency } from '@/lib/market';
import remarkGfm from 'remark-gfm';
import PortfolioChart from '@/components/PortfolioChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkflowPage = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('generate');
  const [clients, setClients] = useState([]);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);

  // Review Cases (Stage 1 case shell -> Stage 11 close review) state
  const [reviewCases, setReviewCases] = useState([]);
  const [caseDashboard, setCaseDashboard] = useState(null);
  const [closingCaseId, setClosingCaseId] = useState(null);
  const [closeForm, setCloseForm] = useState({ client_feedback: '', final_action: 'accepted' });
  const [newCaseForm, setNewCaseForm] = useState({ client_id: '', report_type: 'client_review', case_notes: '' });
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [pptGenerating, setPptGenerating] = useState(false);
  const [isPptLoading, setIsPptLoading] = useState(false);
  const [gammaUrl, setGammaUrl] = useState(null);
  const [showPresentation, setShowPresentation] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatedPPTs, setGeneratedPPTs] = useState(new Map()); // Track PPT URLs for each report
  const [loadingPPTs, setLoadingPPTs] = useState(new Set()); // Track which reports are currently generating PPTs
  const [finalPPTUrls, setFinalPPTUrls] = useState(new Map()); // Track final redirected URLs
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  const formatIndianCurrency = (amount) => marketFormatCurrency(amount, user?.market);

  // Email sending function
  const sendReportViaEmail = async (report) => {
    if (!report.id) {
      toast.error('Report ID not found');
      return;
    }

    setSendingEmail(true);
    try {
      // Pass client_id so backend can resolve the user; also pass email if available
      const params = new URLSearchParams();
      if (report.client_id) params.append('client_id', report.client_id);
      if (report.client_email || report.email) params.append('recipient_email', report.client_email || report.email);

      const response = await axios.post(
        `${API}/reports/${report.id}/send-email?${params.toString()}`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Report sent successfully');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };
  
  // WhatsApp sending function
  const sendReportViaWhatsApp = async (report) => {
    if (!report.id) {
      toast.error('Report ID not found');
      return;
    }

    setSendingWhatsApp(true);
    try {
      const params = new URLSearchParams();
      if (report.client_id) params.append('client_id', report.client_id);
      if (report.client_email || report.email) params.append('recipient_email', report.client_email || report.email);

      const response = await axios.post(
        `${API}/reports/${report.id}/send-whatsapp?${params.toString()}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Open WhatsApp Web URL in new tab
        window.open(response.data.whatsapp_url, '_blank');
        toast.success(response.data.message || 'WhatsApp opened with pre-filled message');
      } else {
        toast.error(response.data.message || 'Failed to generate WhatsApp link');
      }
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      toast.error('Failed to generate WhatsApp link: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Indian currency formatter implemented

  // Function to save PPT data to database
  const savePPTToDB = async (reportId, pptUrl) => {
    try {
      console.log('Saving PPT to database:', { reportId, pptUrl });
      
      await axios.post(
        `${API}/save-ppt-url`,
        {
          report_id: reportId,
          ppt_url: pptUrl
        },
        { withCredentials: true }
      );
      
      // Update local state
      setGeneratedPPTs(prev => new Map(prev).set(reportId, pptUrl));
      console.log('PPT URL saved to database successfully');
    } catch (error) {
      console.warn('Failed to save PPT data to database:', error);
      // Still update state even if DB save fails
      setGeneratedPPTs(prev => new Map(prev).set(reportId, pptUrl));
    }
  };

  // Function to detect and convert URL to embeddable format
  const detectFinalUrl = async (originalUrl) => {
    try {
      const response = await axios.get(`${API}/resolve-final-url`, {
        params: { url: originalUrl },
        withCredentials: true,
        timeout: 15000 // 15 seconds timeout
      });
      
      let finalUrl = response.data.final_url;
      console.log('Server-side URL resolution:', {
        original: originalUrl,
        final: finalUrl,
        different: originalUrl !== finalUrl
      });
      
      // Convert to embeddable format for iframe
      if (finalUrl.includes('gamma.app/docs/')) {
        // Extract document ID from URL
        const docId = finalUrl.split('/docs/')[1].split('?')[0].split('#')[0];
        // Create embed URL
        const embedUrl = `https://gamma.app/embed/${docId}`;
        console.log('Converted to embed URL:', embedUrl);
        return embedUrl;
      }
      
      return finalUrl;
    } catch (error) {
      console.warn('Failed to resolve final URL server-side:', error);
      return originalUrl; // Fallback to original URL
    }
  };

  // Function to load PPT data from database
  const loadPPTsFromDB = async () => {
    try {
      const response = await axios.get(`${API}/get-ppt-urls`, { withCredentials: true });
      
      const pptMap = new Map();
      response.data.ppt_urls.forEach(item => {
        pptMap.set(item.report_id, item.ppt_url);
      });
      
      setGeneratedPPTs(pptMap);
      console.log('Loaded PPT URLs from database:', pptMap.size, 'entries');
    } catch (error) {
      console.warn('Failed to load PPT data from database:', error);
    }
  };
  const [formData, setFormData] = useState({
    client_or_prospect_id: '',
    report_level: 'Client',
    risk_profile_id: '',
    time_period_type: 'as_on_date',
    as_on_date: new Date().toISOString().split('T')[0],
    start_date: '',
    end_date: '',
    report_type: 'Portfolio Review Clients I Exhaustive',
  });

  useEffect(() => {
    fetchData();
    if (activeTab === 'history') {
      fetchReportHistory();
      loadPPTsFromDB(); // Load PPT URLs from database
    }
    if (activeTab === 'cases') {
      fetchReviewCases();
      fetchCaseDashboard();
    }
  }, [activeTab]);

  const fetchReviewCases = async () => {
    try {
      const { data } = await axios.get(`${API}/review-cases`, { withCredentials: true });
      setReviewCases(data || []);
    } catch (error) {
      console.error('Error fetching review cases:', error);
    }
  };

  const fetchCaseDashboard = async () => {
    try {
      const { data } = await axios.get(`${API}/review-cases/dashboard`, { withCredentials: true });
      setCaseDashboard(data);
    } catch (error) {
      console.error('Error fetching case dashboard:', error);
    }
  };

  const createReviewCase = async () => {
    if (!newCaseForm.client_id) {
      toast.error('Select a client');
      return;
    }
    try {
      const client = clients.find(c => c.id === newCaseForm.client_id);
      const { data } = await axios.post(`${API}/review-cases`, {
        ...newCaseForm,
        client_name: client?.full_name || client?.client_name || client?.email || 'Unknown Client',
      }, { withCredentials: true });
      toast.success(`Case ${data.id.slice(0, 8)} created`);
      setNewCaseForm({ client_id: '', report_type: 'client_review', case_notes: '' });
      await fetchReviewCases();
      await fetchCaseDashboard();
    } catch (error) {
      console.error('Error creating review case:', error);
      toast.error(error.response?.data?.detail || 'Failed to create case');
    }
  };

  const updateCaseStatus = async (caseId, status) => {
    try {
      await axios.put(`${API}/review-cases/${caseId}/status`, { status }, { withCredentials: true });
      toast.success('Case status updated');
      await fetchReviewCases();
      await fetchCaseDashboard();
    } catch (error) {
      console.error('Error updating case status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const closeReviewCase = async (caseId) => {
    if (!closeForm.client_feedback.trim()) {
      toast.error('Client feedback is required to close a case');
      return;
    }
    try {
      await axios.post(`${API}/review-cases/${caseId}/close`, closeForm, { withCredentials: true });
      toast.success('Review case closed');
      setClosingCaseId(null);
      setCloseForm({ client_feedback: '', final_action: 'accepted' });
      await fetchReviewCases();
      await fetchCaseDashboard();
    } catch (error) {
      console.error('Error closing review case:', error);
      toast.error(error.response?.data?.detail || 'Failed to close case');
    }
  };
  // Removed fallback handling - direct presentation display only

  // Click outside handler removed - no longer needed for separate buttons

  const fetchData = async () => {
    try {
      const config = { withCredentials: true };
      
      const [clientsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/rm/users`, config),
        axios.get(`${API}/risk-profiles`, config),
      ]);
      setClients(clientsRes.data);
      setRiskProfiles(profilesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportHistory = async () => {
    try {
      const config = { withCredentials: true };
      
      // Fetch report history and user data in parallel
      const [reportResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/workflow/report-history`, config),
        axios.get(`${API}/rm/users`, config)
      ]);
      
      const reports = reportResponse.data;
      const users = usersResponse.data;
      
      // Create a map for quick user lookup by id
      const userMap = new Map();
      users.forEach(user => {
        userMap.set(user.id, user);
      });
      
      // Enrich reports with current client risk profile data
      const enrichedReports = reports.map(report => {
        const client = userMap.get(report.client_id);
        
        // If we have client data and the report is missing risk profile info, enrich it
        if (client && !report.client_original_risk_profile_name) {
          return {
            ...report,
            client_original_risk_profile_name: client.risk_profile || 'Not Determined',
            // Also add some additional context for debugging
            enriched_from_current_data: true
          };
        }
        
        return report;
      });
      
      setReportHistory(enrichedReports);
    } catch (error) {
      console.error('Error fetching report history:', error);
      toast.error('Failed to fetch report history');
    }
  };

  const viewHistoryReport = async (reportId) => {
    try {
      const config = { withCredentials: true };
      const response = await axios.get(`${API}/workflow/report/${reportId}`, config);
      setSelectedHistoryReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const generatePPTDirect = async () => {
    try {
      setPptGenerating(true);
      
      
      // Debug: Log the report object to understand its structure
      console.log('Full report object:', report);
      console.log('Report client:', report?.client);
      console.log('Report content:', report?.report_content);
      console.log('FormData report type:', formData?.report_type);
      
      // Extract text content from the report with correct property names
      const clientName = report?.client?.client_name || report?.client?.full_name || report?.client_name || 'Unknown Client';
      const reportDate = report?.generated_at ? new Date(report.generated_at).toLocaleDateString() : new Date().toLocaleDateString();
      
      // Build recommendations text
      let recommendationsText = '';
      if (report?.recommendations && report.recommendations.length > 0) {
        recommendationsText = '\n\nRecommendations:\n' + report.recommendations.map(rec => 
          `- ${rec.product_name || 'Product'} (${rec.product_type || 'Type'}): ${rec.recommendation_type || 'Recommendation'}`
        ).join('\n');
      }
      
      const reportText = `
        ${formData?.report_type || 'Investment Report'}
        
        Client: ${clientName}
        Report Date: ${reportDate}
        
        ${report?.report_content || 'No content available'}
        ${recommendationsText}
      `;
      
      console.log('Prepared report text:', reportText);
      console.log('Client name for API:', clientName);
      
      // Set up a timeout for 5 minutes (300 seconds)
      const timeoutId = setTimeout(() => {
        if (pptGenerating) {
          toast.error('PPT generation is taking longer than expected. Please try again later.');
          setPptGenerating(false);
        }
      }, 300000); // 5 minutes
      
      try {
        const payload = {
          text: reportText.trim(),
          client_name: clientName,
          user_prompt: "Generate 15 slides, that includes, graphs, charts, tables, some combined wherever necessary also text is going to be there"
        };
        
        console.log('API Payload being sent:', payload);
        
        const response = await axios.post(
          `${API}/generate-ppt`,
          payload,
          {
            withCredentials: true,
            timeout: 300000 // 5 minutes timeout
          }
        );

        // Clear timeout since we got response
        clearTimeout(timeoutId);

        if (response.data.gamma_url) {
          // Debug: Log the URL to console
          console.log('Gamma URL received:', response.data.gamma_url);
          
          // Store the generated PPT URL for the current report if available
          if (report && report.report_id) {
            await savePPTToDB(report.report_id, response.data.gamma_url);
          }
          
          // Show success message
          toast.success('✅ Presentation generated successfully!');
          
          // Now show the same waiting/processing experience as "View PPT"
          setShowPresentation(true);
          setGammaUrl(null); // Clear while processing
          toast.success('Processing presentation URL...');
          
          try {
            // Get the final processed URL (same as View PPT logic)
            const finalUrl = await detectFinalUrl(response.data.gamma_url);
            console.log('Final processed URL:', finalUrl);
            setGammaUrl(finalUrl);
            toast.success('Presentation ready to display!');
          } catch (error) {
            console.error('Error processing URL:', error);
            setGammaUrl(response.data.gamma_url); // Fallback to original
            toast.warning('Using original URL as fallback');
          }
        } else {
          throw new Error('No presentation URL received');
        }
      } catch (apiError) {
        clearTimeout(timeoutId);
        throw apiError;
      }
    } catch (error) {
      console.error('Error generating PPT:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('PPT generation timed out. The process may still be running in background.');
      } else {
        // Handle error response properly - ensure it's a string
        let errorMessage = 'Failed to generate presentation';
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = typeof error.response.data.detail === 'string' 
              ? error.response.data.detail 
              : JSON.stringify(error.response.data.detail);
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
      }
    } finally {
      setPptGenerating(false);
    }
  };

  const generatePPTFromHistory = async () => {
    try {
      setIsPptLoading(true);
      
      
      // Extract text content from the selected history report
      const reportText = `
        ${selectedHistoryReport.report_type}
        
        Client: ${selectedHistoryReport.client_name}
        Report Date: ${new Date(selectedHistoryReport.generated_at).toLocaleDateString()}
        
        ${selectedHistoryReport.report_content}
      `;
      
      // Debug: Log the content being sent
      console.log('Report content being sent to PPT generation:');
      console.log('Report type:', selectedHistoryReport.report_type);
      console.log('Client name:', selectedHistoryReport.client_name);
      console.log('Report content length:', selectedHistoryReport.report_content?.length || 0);
      console.log('First 500 chars of content:', selectedHistoryReport.report_content?.substring(0, 500) || 'No content');
      console.log('Full reportText length:', reportText.length);
      
      // Set up a timeout for 5 minutes (300 seconds)
      const timeoutId = setTimeout(() => {
        if (isPptLoading) {
          toast.error('PPT generation is taking longer than expected. Please try again later.');
          setIsPptLoading(false);
        }
      }, 300000); // 5 minutes
      
      try {
        const response = await axios.post(
          `${API}/generate-ppt`,
          {
            text: reportText.trim(),
            client_name: selectedHistoryReport.client_name,
            user_prompt: "Generate 15 slides, that includes, graphs, charts, tables, some combined wherever necessary also text is going to be there"
          },
          {
            withCredentials: true,
            timeout: 300000 // 5 minutes timeout
          }
        );

        // Clear timeout since we got response
        clearTimeout(timeoutId);

        if (response.data.gamma_url) {
          // Debug: Log the URL to console
          console.log('Gamma URL received:', response.data.gamma_url);
          
          // Show success message and display presentation in UI
          toast.success('✅ Presentation generated successfully!');
          
          // Set the gamma URL to display in the UI
          setGammaUrl(response.data.gamma_url);
          setShowPresentation(true);
          
          // Store the generated PPT URL for this report
          if (selectedHistoryReport) {
            await savePPTToDB(selectedHistoryReport.id, response.data.gamma_url);
          }
        } else {
          throw new Error('No presentation URL received');
        }
      } catch (apiError) {
        clearTimeout(timeoutId);
        throw apiError;
      }
    } catch (error) {
      console.error('Error generating PPT:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('PPT generation timed out. The process may still be running in background.');
      } else {
        // Handle error response properly - ensure it's a string
        let errorMessage = 'Failed to generate presentation';
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = typeof error.response.data.detail === 'string' 
              ? error.response.data.detail 
              : JSON.stringify(error.response.data.detail);
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
      }
    } finally {
      setIsPptLoading(false);
    }
  };

  // PPT modal handler removed - now using direct generation

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const config = { withCredentials: true };
      const response = await axios.post(`${API}/workflow/generate-report`, formData, config);
      setReport(response.data);
      
      // Load PPT data from database to check if PPT already exists for this report
      await loadPPTsFromDB();
      
      toast.success('Report generated successfully');
      // Refresh history if on history tab
      if (activeTab === 'history') {
        fetchReportHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const reportTypes = [
    'Investment Proposal for New Prospects',
    'Portfolio Review + Proposal I Prospect I Holdings Data Only',
    'Portfolio Review + Proposal I Prospect I Txns Data only',
    'Portfolio Review + Proposal I Prospect I Holdings + Txn Data',
    'Portfolio Review Clients I Exhaustive',
    'Portfolio Review Clients I Summary',
    'Deviation Handling Report',
    'Portfolio Rebalancing Report',
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="workflow-container">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
            Generate Report
          </h1>
          <p className="text-gray-600">Create investment proposals and portfolio reviews</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('generate')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'generate'
                  ? 'border-ugmonk-yellow text-ugmonk-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Generate Report
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'history'
                  ? 'border-ugmonk-yellow text-ugmonk-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Report History
            </button>
            <button
              onClick={() => setActiveTab('cases')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'cases'
                  ? 'border-ugmonk-yellow text-ugmonk-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Briefcase className="w-4 h-4 inline mr-2" />
              Review Cases
            </button>
          </div>
        </div>

        {/* Review Cases Tab (Stage 1 case shell -> Stage 11 close review) */}
        {activeTab === 'cases' && (
          <div className="space-y-6">
            {caseDashboard && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-gray-200"><CardContent className="p-4">
                  <p className="text-xs text-gray-500">Total Cases</p>
                  <p className="text-2xl font-bold text-gray-900">{caseDashboard.total_cases}</p>
                </CardContent></Card>
                <Card className="border-gray-200"><CardContent className="p-4">
                  <p className="text-xs text-gray-500">Closed</p>
                  <p className="text-2xl font-bold text-green-700">{caseDashboard.closed_count}</p>
                </CardContent></Card>
                <Card className="border-gray-200"><CardContent className="p-4">
                  <p className="text-xs text-gray-500">Pending Review</p>
                  <p className="text-2xl font-bold text-yellow-700">{caseDashboard.by_status?.pending_review || 0}</p>
                </CardContent></Card>
                <Card className="border-gray-200"><CardContent className="p-4">
                  <p className="text-xs text-gray-500">Avg Days to Close</p>
                  <p className="text-2xl font-bold text-gray-900">{caseDashboard.avg_days_to_close ?? '-'}</p>
                </CardContent></Card>
              </div>
            )}

            <Card className="border-gray-200">
              <CardHeader><CardTitle className="text-gray-900">Create Review Case</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <select
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    value={newCaseForm.client_id}
                    onChange={(e) => setNewCaseForm(prev => ({ ...prev, client_id: e.target.value }))}
                  >
                    <option value="">Select client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.full_name || client.client_name || client.email || 'Unnamed Client'}
                      </option>
                    ))}
                  </select>
                  <select
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    value={newCaseForm.report_type}
                    onChange={(e) => setNewCaseForm(prev => ({ ...prev, report_type: e.target.value }))}
                  >
                    <option value="investment_proposal">Investment Proposal</option>
                    <option value="prospect_review">Prospect Review</option>
                    <option value="client_review">Client Review</option>
                    <option value="summary_review">Summary Review</option>
                    <option value="exhaustive_review">Exhaustive Review</option>
                  </select>
                  <Input
                    placeholder="Case notes (optional)"
                    value={newCaseForm.case_notes}
                    onChange={(e) => setNewCaseForm(prev => ({ ...prev, case_notes: e.target.value }))}
                  />
                </div>
                <Button onClick={createReviewCase}>
                  <Plus className="w-4 h-4 mr-2" /> Create Case
                </Button>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader><CardTitle className="text-gray-900">Cases ({reviewCases.length})</CardTitle></CardHeader>
              <CardContent>
                {reviewCases.length === 0 ? (
                  <p className="text-gray-600 text-sm">No review cases yet.</p>
                ) : (
                  <div className="space-y-3">
                    {reviewCases.map((c) => (
                      <div key={c.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-900">{c.client_name} - {c.report_type.replace('_', ' ')}</p>
                          <Badge className={
                            c.status === 'closed' ? 'bg-green-100 text-green-800' :
                            c.status === 'escalated' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }>
                            {c.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">Case ID: {c.id} · Created {new Date(c.created_at).toLocaleDateString()}</p>
                        {c.case_notes && <p className="text-sm text-gray-600 mb-2">{c.case_notes}</p>}
                        {c.status === 'closed' ? (
                          <div className="bg-green-50 border border-green-200 rounded p-2 text-sm">
                            <p className="text-green-800 font-medium">Closed - {c.final_action}</p>
                            <p className="text-green-700">{c.client_feedback}</p>
                          </div>
                        ) : closingCaseId === c.id ? (
                          <div className="space-y-2 border-t pt-2">
                            <Textarea
                              placeholder="Client feedback (required)"
                              value={closeForm.client_feedback}
                              onChange={(e) => setCloseForm(prev => ({ ...prev, client_feedback: e.target.value }))}
                            />
                            <select
                              className="border border-gray-300 rounded px-2 py-1 text-sm"
                              value={closeForm.final_action}
                              onChange={(e) => setCloseForm(prev => ({ ...prev, final_action: e.target.value }))}
                            >
                              <option value="accepted">Accepted</option>
                              <option value="deferred">Deferred</option>
                              <option value="rejected">Rejected</option>
                            </select>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => closeReviewCase(c.id)}>
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Close Case
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setClosingCaseId(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {['pending_data', 'pending_risk_profile', 'pending_review', 'escalated', 'approved', 'finalized'].map((s) => (
                              <Button key={s} size="sm" variant="outline" onClick={() => updateCaseStatus(c.id, s)}>
                                {s.replace('_', ' ')}
                              </Button>
                            ))}
                            <Button size="sm" className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" onClick={() => setClosingCaseId(c.id)}>
                              Close Review
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Generate Report Tab */}
        {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <Card className="lg:col-span-1 border-gray-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-gray-900">Input Selection Criteria</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="workflow-form">
                <div>
                  <Label htmlFor="client">Client/Prospect *</Label>
                  <select
                    id="client"
                    value={formData.client_or_prospect_id}
                    onChange={(e) => setFormData({ ...formData, client_or_prospect_id: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="client-select"
                  >
                    <option value="">Select...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.full_name || client.client_name || client.email || 'Unnamed Client'} ({client.user_type || client.type || 'client'})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="report_level">Report Level *</Label>
                  <select
                    id="report_level"
                    value={formData.report_level}
                    onChange={(e) => setFormData({ ...formData, report_level: e.target.value })}
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="report-level-select"
                  >
                    <option value="Group">Group</option>
                    <option value="Client">Client</option>
                    <option value="Account">Account</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="risk_profile">Risk Profile</Label>
                  <select
                    id="risk_profile"
                    value={formData.risk_profile_id}
                    onChange={(e) => setFormData({ ...formData, risk_profile_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="risk-profile-select"
                  >
                    <option value="">Use client's profile...</option>
                    {riskProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="time_period">Time Period *</Label>
                  <select
                    id="time_period"
                    value={formData.time_period_type}
                    onChange={(e) => setFormData({ ...formData, time_period_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="time-period-select"
                  >
                    <option value="as_on_date">As on Date</option>
                    <option value="date_range">Date Range</option>
                  </select>
                </div>

                {formData.time_period_type === 'as_on_date' ? (
                  <div>
                    <Label htmlFor="as_on_date">As on Date *</Label>
                    <Input
                      id="as_on_date"
                      type="date"
                      value={formData.as_on_date}
                      onChange={(e) => setFormData({ ...formData, as_on_date: e.target.value })}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                      data-testid="as-on-date-input"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900"
                        data-testid="start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900"
                        data-testid="end-date-input"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="report_type">Report Type *</Label>
                  <select
                    id="report_type"
                    value={formData.report_type}
                    onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="report-type-select"
                  >
                    {reportTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={generating} data-testid="generate-report-button">
                  {generating ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Report Output */}
          <Card className="lg:col-span-2 border-gray-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-900">Generated Report</CardTitle>
                {report && report.report_content && report.client && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="border-ugmonk-yellow text-ugmonk-grey" 
                      onClick={() => {
                        // Handle PDF download
                        console.log('Download PDF');
                      }}
                      data-testid="download-report-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    
                    {/* Conditional PPT Button - similar to history tab */}
                    {report.report_id && generatedPPTs.has(report.report_id) ? (
                      <Button 
                        variant="outline" 
                        className="border-ugmonk-yellow text-ugmonk-grey" 
                        onClick={async () => {
                          // Get and process the final presentation URL
                          const originalUrl = generatedPPTs.get(report.report_id);
                          console.log('Getting final presentation URL from:', originalUrl);
                          
                          setShowPresentation(true);
                          setGammaUrl(null); // Clear while processing
                          toast.success('Processing presentation URL...');
                          
                          try {
                            // Get the final processed URL
                            const finalUrl = await detectFinalUrl(originalUrl);
                            console.log('Final processed URL:', finalUrl);
                            setGammaUrl(finalUrl);
                            toast.success('Presentation ready to display!');
                          } catch (error) {
                            console.error('Error processing URL:', error);
                            setGammaUrl(originalUrl); // Fallback to original
                            toast.warning('Using original URL as fallback');
                          }
                        }}
                        data-testid="view-ppt-button"
                      >
                        <Presentation className="w-4 h-4 mr-2" />
                        View PPT
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="border-ugmonk-yellow text-ugmonk-grey" 
                        onClick={() => generatePPTDirect()}
                        disabled={pptGenerating || !report.report_content || !report.client}
                        data-testid="generate-ppt-button"
                      >
                        {pptGenerating ? (
                          <>
                            <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                            Generating PPT...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" />
                            Generate PPT
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!report ? (
                <div className="text-center py-16">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-gray-900 text-lg font-semibold mb-2">No Report Generated</h3>
                  <p className="text-gray-600">Fill in the criteria and click Generate Report</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Report Header */}
                  <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{formData.report_type}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Client</p>
                        <p className="text-gray-900 font-semibold">{report.client.client_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Type</p>
                        <p className="text-gray-900 font-semibold capitalize">{report.client.type}</p>
                      </div>
                      {report.risk_profile && (
                        <div>
                          <p className="text-gray-600">Risk Profile</p>
                          <p className="text-gray-900 font-semibold">{report.risk_profile.name}</p>
                        </div>
                      )}
                      {report.portfolio && report.portfolio.total_value && (
                        <div>
                          <p className="text-gray-600">Portfolio Value</p>
                          <p className="text-gray-900 font-semibold">{formatIndianCurrency(report.portfolio.total_value)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Input Details Section */}
                  <Card className="border-ugmonk-border bg-[#fffdf0]/50">
                    <CardHeader>
                      <CardTitle className="text-gray-900 flex items-center gap-2 text-lg">
                        <FileText className="w-5 h-5 text-ugmonk-dark" />
                        Report Generation Inputs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Risk Profile Details */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900 border-b border-ugmonk-border pb-2">Risk Profile Information</h4>
                          <div className="space-y-2 text-sm">
                            {/* Client's Original Profile from Security Master */}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Client's Original Profile:</span>
                              <span className="font-medium text-gray-900">
                                {report.client_original_risk_profile_name || 'Not Set in Security Master'}
                              </span>
                            </div>
                            
                            {/* Selected Profile for Report */}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Used for Report:</span>
                              <span className="font-medium text-gray-900">
                                {report.selected_risk_profile_name || report.risk_profile?.name || 'Default'}
                              </span>
                            </div>
                            
                            {/* Modification Status */}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <span className={`font-medium px-3 py-1 rounded-full text-sm ${
                                report.risk_profile_modified 
                                  ? 'bg-[#fffdf0] text-ugmonk-darker border border-ugmonk-border' 
                                  : 'bg-green-100 text-green-800 border border-green-200'
                              }`}>
                                {report.risk_profile_modified ? 'RM Override Applied' : 'Client Original Used'}
                              </span>
                            </div>
                            
                            {/* Profile Details */}
                            {report.risk_profile && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Risk Level:</span>
                                  <span className="font-medium text-gray-900">{report.risk_profile.risk_level || 'Not specified'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Equity Allocation:</span>
                                  <span className="font-medium text-gray-900">{report.risk_profile.equity_allocation || 0}%</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Report Configuration */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900 border-b border-ugmonk-border pb-2">Report Configuration</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Report Level:</span>
                              <span className="font-medium text-gray-900">{formData.report_level}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Time Period:</span>
                              <span className="font-medium text-gray-900">
                                {formData.time_period_type === 'as_on_date' 
                                  ? `As on ${new Date(formData.as_on_date).toLocaleDateString()}`
                                  : `${new Date(formData.start_date).toLocaleDateString()} to ${new Date(formData.end_date).toLocaleDateString()}`
                                }
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Generated At:</span>
                              <span className="font-medium text-gray-900">{new Date(report.generated_at).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Generated By:</span>
                              <span className="font-medium text-gray-900">{user?.email || 'RM'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Additional Input Details */}
                      {(formData.specific_holdings || formData.additional_instructions) && (
                        <div className="mt-6 pt-4 border-t border-ugmonk-border">
                          <h4 className="font-semibold text-gray-900 mb-3">Additional Inputs</h4>
                          <div className="space-y-2 text-sm">
                            {formData.specific_holdings && (
                              <div>
                                <span className="text-gray-600">Specific Holdings Analysis:</span>
                                <p className="text-gray-900 mt-1 bg-white p-2 rounded border">{formData.specific_holdings}</p>
                              </div>
                            )}
                            {formData.additional_instructions && (
                              <div>
                                <span className="text-gray-600">Additional Instructions:</span>
                                <p className="text-gray-900 mt-1 bg-white p-2 rounded border">{formData.additional_instructions}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Portfolio Chart */}
                  {report.portfolio && report.portfolio.holdings && report.portfolio.holdings.length > 0 && (
                    <Card className="border-gray-200 bg-white">
                      <CardHeader>
                        <CardTitle className="text-gray-900 flex items-center gap-2">
                          <PieChart className="w-5 h-5 text-ugmonk-dark" />
                          Asset Allocation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <PortfolioChart portfolio={report.portfolio} />
                      </CardContent>
                    </Card>
                  )}

                  {/* AI Generated Content */}
                  <Card className="border-ugmonk-border bg-gradient-to-br from-[#fffdf0] to-[#f8f6f1]">
                    <CardHeader>
                      <CardTitle className="text-gray-900 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-ugmonk-dark" />
                        AI-Generated Report Content
                      </CardTitle>
                      {report.attached_market_outlook && (
                        <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                          Includes approved market outlook: <span className="font-medium text-ugmonk-dark">{report.attached_market_outlook.title}</span> (v{report.attached_market_outlook.version})
                        </p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="prose max-w-none text-gray-800 leading-relaxed space-y-4">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-gray-900 mb-4" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2" {...props} />,
                            p: ({node, ...props}) => <p className="text-gray-800 mb-3 leading-relaxed" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc list-outside text-gray-800 space-y-2 ml-6 pl-2" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal list-outside text-gray-800 space-y-2 ml-6 pl-2" {...props} />,
                            li: ({node, children, ...props}) => (
                              <li className="text-gray-800 leading-relaxed" {...props}>
                                {children}
                              </li>
                            ),
                            strong: ({node, ...props}) => <strong className="text-ugmonk-dark font-semibold" {...props} />,
                            em: ({node, ...props}) => <em className="text-gray-700 italic" {...props} />,
                            code: ({node, ...props}) => <code className="bg-gray-200 px-1.5 py-0.5 rounded text-ugmonk-darker text-sm" {...props} />,
                            table: ({node, ...props}) => (
                              <div className="overflow-x-auto my-4">
                                <table className="min-w-full border-collapse border border-gray-300 text-sm" {...props} />
                              </div>
                            ),
                            thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
                            tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200" {...props} />,
                            tr: ({node, ...props}) => <tr className="hover:bg-gray-50" {...props} />,
                            th: ({node, ...props}) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900 bg-gray-100" {...props} />,
                            td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2 text-gray-700" {...props} />,
                          }}
                        >
                          {report.report_content}
                        </ReactMarkdown>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  {report.recommendations && report.recommendations.length > 0 && (
                    <Card className="border-gray-200 bg-white">
                      <CardHeader>
                        <CardTitle className="text-gray-900">Applicable Recommendations</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {report.recommendations.map((rec) => (
                            <div key={rec.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="text-gray-900 font-semibold">{rec.product_name}</h4>
                                  <p className="text-gray-600 text-sm">{rec.product_type}</p>
                                </div>
                                <span className="px-3 py-1 bg-[#fffdf0] text-ugmonk-dark rounded-full text-xs">
                                  {rec.recommendation_type}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <Card className="border-gray-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-900">Report History</CardTitle>
              </CardHeader>
              <CardContent>
                {reportHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-gray-900 text-lg font-semibold mb-2">No Reports Generated Yet</h3>
                    <p className="text-gray-600">Generated reports will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reportHistory.map((historyReport) => (
                      <div
                        key={historyReport.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-ugmonk-border transition-colors cursor-pointer bg-white"
                        onClick={() => viewHistoryReport(historyReport.id)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h4 className="text-gray-900 font-semibold mb-1">{historyReport.client_name}</h4>
                            <p className="text-sm text-gray-600 mb-2">{historyReport.report_type}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>Value: {formatIndianCurrency(historyReport.portfolio_value || 0)}</span>
                              <span>•</span>
                              <span>{new Date(historyReport.generated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>

                        {/* Input Details Section */}
                        <div className="bg-gray-50 rounded-lg p-3 mb-3 border-l-4 border-ugmonk-yellow">
                          <h5 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">
                            <FileText className="w-4 h-4 text-ugmonk-dark" />
                            Report Generation Details
                          </h5>
                          <div className="grid grid-cols-2 gap-4">
                            {/* Risk Profile Column */}
                            <div className="space-y-1">
                              <h6 className="text-xs font-medium text-gray-700 mb-1">Risk Profile</h6>
                              <div className="space-y-0.5 text-xs">
                                {/* Client's Original Profile */}
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Client's Profile:</span>
                                  <span className="font-medium text-gray-900">
                                    {historyReport.client_original_risk_profile_name || 'Not Set'}
                                  </span>
                                </div>
                                
                                {/* Selected Profile (if different) */}
                                {historyReport.risk_profile_modified && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">RM Selected:</span>
                                    <span className="font-medium text-ugmonk-dark">
                                      {historyReport.selected_risk_profile_name}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Status Indicator */}
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Status:</span>
                                  <span className={`font-medium text-xs px-2 py-0.5 rounded-full ${
                                    historyReport.risk_profile_modified 
                                      ? 'bg-[#fffdf0] text-ugmonk-darker' 
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {historyReport.risk_profile_modified ? 'RM Modified' : 'Client Original'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Report Configuration Column */}
                            <div className="space-y-1">
                              <h6 className="text-xs font-medium text-gray-700 mb-1">Configuration</h6>
                              <div className="space-y-0.5 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Level:</span>
                                  <span className="font-medium text-gray-900">{historyReport.report_level || 'Standard'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Period:</span>
                                  <span className="font-medium text-gray-900">
                                    {historyReport.time_period_type === 'as_on_date' 
                                      ? `As on ${new Date(historyReport.as_on_date || historyReport.generated_at).toLocaleDateString()}`
                                      : 'Date Range'
                                    }
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Generated:</span>
                                  <span className="font-medium text-gray-900">{new Date(historyReport.generated_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex-1"></div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-ugmonk-yellow text-ugmonk-dark hover:bg-[#fffdf0]"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedHistoryReport(historyReport);
                                setShowReportModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Report
                            </Button>
                            
                            {/* Email Send Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-green-500 text-green-600 hover:bg-green-50"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await sendReportViaEmail(historyReport);
                              }}
                              disabled={sendingEmail}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Email
                            </Button>
                            
                            {/* WhatsApp Send Button */}
                            <Button
                              variant="outline"  
                              size="sm"
                              className="border-green-500 text-green-600 hover:bg-green-50"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await sendReportViaWhatsApp(historyReport);
                              }}
                              disabled={sendingWhatsApp}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              WhatsApp
                            </Button>
                            
                            {/* Conditional PPT Button */}
                            {generatedPPTs.has(historyReport.id) ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-ugmonk-yellow text-ugmonk-dark hover:bg-[#fffdf0]"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Get and process the final presentation URL
                                  const originalUrl = generatedPPTs.get(historyReport.id);
                                  console.log('Getting final presentation URL from:', originalUrl);
                                  
                                  setShowPresentation(true);
                                  setGammaUrl(null); // Clear while processing
                                  toast.success('Processing presentation URL...');
                                  
                                  try {
                                    // Get the final processed URL
                                    const finalUrl = await detectFinalUrl(originalUrl);
                                    console.log('Final processed URL:', finalUrl);
                                    setGammaUrl(finalUrl);
                                    toast.success('Presentation ready to display!');
                                  } catch (error) {
                                    console.error('Error processing URL:', error);
                                    setGammaUrl(originalUrl); // Fallback to original
                                    toast.warning('Using original URL as fallback');
                                  }
                                }}
                              >
                                <Presentation className="w-4 h-4 mr-1" />
                                View PPT
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-ugmonk-yellow text-ugmonk-dark hover:bg-[#fffdf0]"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  // Add this report to loading set
                                  setLoadingPPTs(prev => new Set(prev).add(historyReport.id));
                                  
                                  try {
                                    
                                    // Extract text content from the selected report
                                    const reportText = `
                                      ${historyReport.report_type}
                                      
                                      Client: ${historyReport.client_name}
                                      Report Date: ${new Date(historyReport.generated_at).toLocaleDateString()}
                                      
                                      ${historyReport.report_content}
                                    `;
                                    
                                    const response = await axios.post(
                                      `${API}/generate-ppt`,
                                      {
                                        text: reportText.trim(),
                                        client_name: historyReport.client_name,
                                        user_prompt: "Generate 15 slides, that includes, graphs, charts, tables, some combined wherever necessary also text is going to be there"
                                      },
                                      {
                                        withCredentials: true,
                                        timeout: 300000 // 5 minutes timeout
                                      }
                                    );

                                    if (response.data.gamma_url) {
                                      const originalUrl = response.data.gamma_url;
                                      console.log('PPT generated for report:', historyReport.id, originalUrl);
                                      
                                      // Save to database
                                      await savePPTToDB(historyReport.id, originalUrl);
                                      
                                      // Show success and process URL for final display
                                      toast.success('✅ Presentation generated! Processing URL...');
                                      setShowPresentation(true);
                                      setGammaUrl(null); // Clear while processing
                                      
                                      try {
                                        // Wait for render and get final URL
                                        const finalUrl = await detectFinalUrl(originalUrl);
                                        console.log('Final processed URL:', finalUrl);
                                        setGammaUrl(finalUrl);
                                        setFinalPPTUrls(prev => new Map(prev).set(historyReport.id, finalUrl));
                                        toast.success('Presentation ready to display!');
                                      } catch (error) {
                                        console.error('Error processing URL:', error);
                                        setGammaUrl(originalUrl); // Fallback to original
                                        toast.warning('Using original URL as fallback');
                                      }
                                    } else {
                                      throw new Error('No presentation URL received');
                                    }
                                  } catch (error) {
                                    console.error('Error generating PPT:', error);
                                    if (error.code === 'ECONNABORTED') {
                                      toast.error('PPT generation timed out. The process may still be running in background.');
                                    } else {
                                      // Handle error response properly - ensure it's a string
                                      let errorMessage = 'Failed to generate presentation';
                                      if (error.response?.data) {
                                        if (typeof error.response.data === 'string') {
                                          errorMessage = error.response.data;
                                        } else if (error.response.data.detail) {
                                          errorMessage = typeof error.response.data.detail === 'string' 
                                            ? error.response.data.detail 
                                            : JSON.stringify(error.response.data.detail);
                                        } else if (error.response.data.message) {
                                          errorMessage = error.response.data.message;
                                        }
                                      } else if (error.message) {
                                        errorMessage = error.message;
                                      }
                                      toast.error(errorMessage);
                                    }
                                  } finally {
                                    // Remove from loading set
                                    setLoadingPPTs(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(historyReport.id);
                                      return newSet;
                                    });
                                  }
                                }}
                                disabled={loadingPPTs.has(historyReport.id)}
                              >
                                {loadingPPTs.has(historyReport.id) ? (
                                  <>
                                    <Sparkles className="w-4 h-4 mr-1 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Generate PPT
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Old report display removed - now using modal */}
          </div>
        )}
      </div>

      {/* Enhanced Report Modal */}
      {showReportModal && selectedHistoryReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-7xl max-h-full flex flex-col">
            {/* Enhanced Header */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-[#fffdf0] to-[#f8f6f1]">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#fffdf0] rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-ugmonk-dark" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Report Details - {selectedHistoryReport.client_name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                    <span className="px-2 py-1 bg-[#fffdf0] text-ugmonk-darker rounded-full text-xs font-medium">
                      {selectedHistoryReport.report_type}
                    </span>
                    <span>•</span>
                    <span>Generated on {new Date(selectedHistoryReport.generated_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long', 
                      year: 'numeric'
                    })}</span>
                  </p>
                </div>
              </div>
              
              {/* Enhanced Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => {
                    toast.info('Generate new report functionality coming soon!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg shadow-sm font-medium transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Generate New Report
                </Button>
                
                <Button
                  onClick={() => {
                    const reportContent = selectedHistoryReport.report_content;
                    const blob = new Blob([reportContent], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedHistoryReport.client_name}_Report.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success('Report downloaded successfully!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg shadow-sm font-medium transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  Download Report
                </Button>
                
                {/* Enhanced PPT Button */}
                {generatedPPTs.has(selectedHistoryReport.id) || finalPPTUrls.has(selectedHistoryReport.id) ? (
                  <Button
                    onClick={async () => {
                      const originalUrl = generatedPPTs.get(selectedHistoryReport.id);
                      if (!originalUrl) return;
                      
                      setShowPresentation(true);
                      setGammaUrl(null);
                      toast.success('Processing presentation URL...');
                      
                      try {
                        const finalUrl = await detectFinalUrl(originalUrl);
                        setGammaUrl(finalUrl);
                        setFinalPPTUrls(prev => new Map(prev).set(selectedHistoryReport.id, finalUrl));
                        toast.success('Presentation ready to display!');
                      } catch (error) {
                        console.error('Error processing URL:', error);
                        setGammaUrl(originalUrl);
                        toast.warning('Using original URL as fallback');
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg shadow-sm font-medium transition-all duration-200"
                  >
                    <Presentation className="w-4 h-4" />
                    View PPT
                  </Button>
                ) : (
                  <Button
                    onClick={async () => {
                      if (loadingPPTs.has(selectedHistoryReport.id)) return;
                      
                      setLoadingPPTs(prev => new Set(prev).add(selectedHistoryReport.id));
                      setIsPptLoading(true);
                      
                      try {
                        const response = await axios.post(
                          `${API}/generate-ppt`,
                          {
                            report_id: selectedHistoryReport.id,
                            report_content: selectedHistoryReport.report_content,
                            client_name: selectedHistoryReport.client_name
                          },
                          { withCredentials: true }
                        );

                        if (response.data.ppt_url) {
                          const originalUrl = response.data.ppt_url;
                          await savePPTToDB(selectedHistoryReport.id, originalUrl);
                          
                          // Wait for render and get final URL
                          toast.success('Presentation generated! Processing URL...');
                          const finalUrl = await detectFinalUrl(originalUrl);
                          setFinalPPTUrls(prev => new Map(prev).set(selectedHistoryReport.id, finalUrl));
                          toast.success('Presentation ready to display!');
                        }
                      } catch (error) {
                        toast.error('Failed to generate presentation');
                      } finally {
                        setLoadingPPTs(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(selectedHistoryReport.id);
                          return newSet;
                        });
                        setIsPptLoading(false);
                      }
                    }}
                    disabled={loadingPPTs.has(selectedHistoryReport.id) || isPptLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-ugmonk-yellow hover:bg-[#f5cc6a] disabled:opacity-50 text-white rounded-lg shadow-sm font-medium transition-all duration-200"
                  >
                    {loadingPPTs.has(selectedHistoryReport.id) || isPptLoading ? (
                      <>
                        <Sparkles className="w-4 h-4 animate-spin" />
                        Generating PPT...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Generate PPT
                      </>
                    )}
                  </Button>
                )}
                
                <Button
                  onClick={() => sendReportViaEmail(selectedHistoryReport)}
                  disabled={sendingEmail}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg shadow-sm font-medium transition-all duration-200"
                >
                  {sendingEmail ? (
                    <>
                      <Sparkles className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => sendReportViaWhatsApp(selectedHistoryReport)}
                  disabled={sendingWhatsApp}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white rounded-lg shadow-sm font-medium transition-all duration-200"
                >
                  {sendingWhatsApp ? (
                    <>
                      <Sparkles className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4" />
                      Send WhatsApp
                    </>
                  )}
                </Button>
                
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedHistoryReport(null);
                  }}
                  className="ml-1 p-2 rounded-lg hover:bg-black/10 transition-colors duration-200 text-gray-600 hover:text-gray-900"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Enhanced Report Content */}
            <div className="flex-1 bg-gray-50 overflow-auto">
              <div className="p-6 space-y-6">
                {/* Enhanced Report Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0] rounded-xl border border-ugmonk-border p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-ugmonk-darker">Portfolio Value</h4>
                      <PieChart className="w-6 h-6 text-ugmonk-dark" />
                    </div>
                    <div className="text-2xl font-bold text-ugmonk-darker mb-2">
                      {formatIndianCurrency(selectedHistoryReport.portfolio_value || 0)}
                    </div>
                    <p className="text-sm text-ugmonk-dark">Total Assets Under Management</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-green-900">Risk Profile</h4>
                      <Eye className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="text-lg font-bold text-green-900 mb-2">
                      {selectedHistoryReport.client_original_risk_profile_name || selectedHistoryReport.selected_risk_profile_name || 'Not Set'}
                    </div>
                    <div className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                      selectedHistoryReport.risk_profile_modified 
                        ? 'bg-[#fffdf0] text-ugmonk-darker' 
                        : 'bg-green-200 text-green-800'
                    }`}>
                      {selectedHistoryReport.risk_profile_modified ? 'RM Override' : 'Client Original'}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-purple-900">Report Level</h4>
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="text-lg font-bold text-purple-900 mb-2">
                      {selectedHistoryReport.report_level || 'Standard'}
                    </div>
                    <p className="text-sm text-purple-700">
                      Generated on {new Date(selectedHistoryReport.generated_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>

                {/* Portfolio Allocation Chart */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <PieChart className="w-6 h-6 text-ugmonk-dark" />
                    Recommended Portfolio Allocation
                  </h4>
                  
                  {/* Dynamic Portfolio Chart based on Risk Profile */}
                  {(() => {
                    const riskProfile = (selectedHistoryReport.client_original_risk_profile_name || selectedHistoryReport.selected_risk_profile_name || '').toLowerCase();
                    let portfolioData = [];
                    
                    if (riskProfile.includes('conservative')) {
                      portfolioData = [
                        { name: 'Fixed Deposits & Bonds', value: 60, color: '#ffd87a' },
                        { name: 'Large Cap Equity',       value: 25, color: '#CCD5AE' },
                        { name: 'Debt Mutual Funds',      value: 10, color: '#d9e2c3' },
                        { name: 'Gold/Commodities',       value: 5,  color: '#EEECC8' }
                      ];
                    } else if (riskProfile.includes('moderate')) {
                      portfolioData = [
                        { name: 'Large Cap Equity',        value: 40, color: '#ffd87a' },
                        { name: 'Fixed Deposits & Bonds',  value: 30, color: '#ff3451' },
                        { name: 'Mid Cap Equity',          value: 15, color: '#d9e2c3' },
                        { name: 'Debt Mutual Funds',       value: 10, color: '#1a1a1a' },
                        { name: 'Gold/Commodities',        value: 5,  color: '#a8a8a8' }
                      ];
                    } else if (riskProfile.includes('aggressive')) {
                      portfolioData = [
                        { name: 'Large Cap Equity',         value: 50, color: '#ffd87a' },
                        { name: 'Mid & Small Cap Equity',   value: 25, color: '#ff3451' },
                        { name: 'International Equity',     value: 10, color: '#d9e2c3' },
                        { name: 'Fixed Deposits & Bonds',   value: 10, color: '#1a1a1a' },
                        { name: 'Alternative Investments',  value: 5,  color: '#a8a8a8' }
                      ];
                    } else {
                      portfolioData = [
                        { name: 'Equity Funds',    value: 45, color: '#ffd87a' },
                        { name: 'Debt Securities', value: 35, color: '#ff3451' },
                        { name: 'Hybrid Funds',    value: 15, color: '#d9e2c3' },
                        { name: 'Others',          value: 5,  color: '#1a1a1a' }
                      ];
                    }
                    
                    return (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Portfolio Chart */}
                        <div className="w-full max-w-md mx-auto">
                          <PortfolioChart 
                            data={portfolioData}
                            width={350} 
                            height={280}
                          />
                        </div>
                        
                        {/* Portfolio Breakdown */}
                        <div className="space-y-4">
                          <h5 className="font-semibold text-gray-800 mb-4">Asset Allocation Breakdown</h5>
                          {portfolioData.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-4 h-4 rounded-full" 
                                  style={{ backgroundColor: item.color }}
                                ></div>
                                <span className="font-medium text-gray-800">{item.name}</span>
                              </div>
                              <span className="font-bold text-gray-900">{item.value}%</span>
                            </div>
                          ))}
                          
                          <div className="mt-6 p-4 bg-[#fffdf0] rounded-lg border border-ugmonk-border">
                            <h6 className="font-semibold text-ugmonk-darker mb-2">Risk Profile Notes</h6>
                            <p className="text-sm text-ugmonk-darker">
                              {riskProfile.includes('conservative') && 
                                'Conservative profile focuses on capital preservation with lower volatility and steady returns.'
                              }
                              {riskProfile.includes('moderate') && 
                                'Moderate profile balances growth and stability with diversified asset allocation.'
                              }
                              {riskProfile.includes('aggressive') && 
                                'Aggressive profile targets high growth potential with higher risk tolerance for equity exposure.'
                              }
                              {!['conservative', 'moderate', 'aggressive'].some(type => riskProfile.includes(type)) &&
                                'Balanced approach with diversified portfolio suitable for general investment goals.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Enhanced Report Content */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <FileText className="w-6 h-6 text-green-600" />
                    Detailed Investment Report
                  </h4>
                  
                  <div className="prose prose-lg max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({children}) => (
                          <h1 className="text-3xl font-bold mb-6 text-gray-900 border-b-2 border-ugmonk-border pb-3">
                            {children}
                          </h1>
                        ),
                        h2: ({children}) => (
                          <h2 className="text-2xl font-bold mb-4 text-gray-800 mt-8 bg-gradient-to-r from-[#fffdf0] to-transparent p-4 rounded-lg border-l-4 border-ugmonk-yellow">
                            {children}
                          </h2>
                        ),
                        h3: ({children}) => (
                          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6 flex items-center gap-2">
                            <div className="w-2 h-6 bg-ugmonk-yellow rounded-full"></div>
                            {children}
                          </h3>
                        ),
                        h4: ({children}) => (
                          <h4 className="text-lg font-semibold mb-2 text-gray-800 mt-4">
                            {children}
                          </h4>
                        ),
                        p: ({children}) => (
                          <p className="mb-4 text-gray-700 leading-relaxed text-base">
                            {children}
                          </p>
                        ),
                        ul: ({children}) => (
                          <ul className="list-none mb-6 space-y-2 bg-gray-50 p-4 rounded-lg border-l-4 border-green-400">
                            {children}
                          </ul>
                        ),
                        ol: ({children}) => (
                          <ol className="list-decimal list-inside mb-6 space-y-2 bg-[#fffdf0] p-4 rounded-lg border-l-4 border-ugmonk-yellow">
                            {children}
                          </ol>
                        ),
                        li: ({children}) => (
                          <li className="text-gray-700 flex items-start gap-2">
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                            <span>{children}</span>
                          </li>
                        ),
                        strong: ({children}) => (
                          <strong className="font-bold text-gray-900 bg-yellow-100 px-1 rounded">
                            {children}
                          </strong>
                        ),
                        em: ({children}) => (
                          <em className="italic text-ugmonk-darker font-medium">
                            {children}
                          </em>
                        ),
                        blockquote: ({children}) => (
                          <blockquote className="border-l-4 border-ugmonk-yellow bg-[#fffdf0] p-4 italic text-ugmonk-darker rounded-r-lg my-4">
                            {children}
                          </blockquote>
                        ),
                        code: ({children}) => (
                          <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono">
                            {children}
                          </code>
                        ),
                        table: ({children}) => (
                          <div className="overflow-x-auto my-4">
                            <table className="min-w-full border-collapse border border-gray-300 text-sm">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({children}) => <thead className="bg-gray-100">{children}</thead>,
                        tbody: ({children}) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
                        tr: ({children}) => <tr className="hover:bg-gray-50">{children}</tr>,
                        th: ({children}) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900 bg-gray-100">{children}</th>,
                        td: ({children}) => <td className="border border-gray-300 px-4 py-2 text-gray-700">{children}</td>,
                      }}
                    >
                      {selectedHistoryReport.report_content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Display embedded presentation */}
      {showPresentation && gammaUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-7xl max-h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">📊 Presentation Viewer</h3>
                <p className="text-sm text-gray-600 mt-1">Embedded presentation within platform</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowPresentation(false);
                    setGammaUrl(null);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-gray-600 hover:text-gray-900"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Direct Presentation Display */}
            <div className="flex-1 bg-white relative">
              {gammaUrl ? (
                <iframe
                  src={gammaUrl}
                  className="w-full h-full border-0 bg-white"
                  title="Presentation"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="no-referrer-when-downgrade"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-presentation"
                  style={{
                    minHeight: '600px',
                    backgroundColor: 'white'
                  }}
                  onLoad={() => {
                    console.log('Final presentation loaded successfully');
                  }}
                />
              ) : (
                /* Processing state while getting final URL */
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#fffdf0] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Presentation className="w-8 h-8 text-ugmonk-dark animate-spin" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Processing Presentation...</h4>
                    <p className="text-gray-600">Getting final presentation URL for optimal display</p>
                    
                    {/* Processing indicator */}
                    <div className="w-48 bg-gray-200 rounded-full h-2 mx-auto mt-4">
                      <div className="bg-ugmonk-yellow h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PPT generation now handled by direct button click */}
    </Layout>
  );
};

export default WorkflowPage;
